import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from "react";
import type {
  SpeechRecognition,
  SpeechRecognitionConstructor,
  SpeechRecognitionErrorEvent,
  SpeechRecognitionEvent,
} from "../types/global";

export type PermissionState = "granted" | "denied" | "prompt";
export const useMicrophone = (
  currentLocale: string | null,
  setText: Dispatch<SetStateAction<string>>
) => {
  const [isListening, setIsListening] = useState(false);
  const [microPhonePermission, setMicroPhonePermission] = useState<PermissionState | null>(null);

  const SpeechRecognition: SpeechRecognitionConstructor | undefined =
    typeof window !== "undefined"
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : undefined;

  const recognition = useMemo<SpeechRecognition | undefined>(() => {
    if (!SpeechRecognition) {
      return undefined;
    }
    return new SpeechRecognition();
  }, [SpeechRecognition]);

  const requestMicrophonePermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicroPhonePermission("granted");
      // we cut the microphone just after we have the permission
      stream.getTracks().forEach((track) => track.stop());
    } catch (err) {
      setMicroPhonePermission("denied");
    }
  }, []);

  const handleCutMicrophone = useCallback(() => {
    setIsListening(false);
    if (!recognition) return;
    recognition.stop();
  }, [recognition, setIsListening]);

  const handleStartMicrophone = useCallback(() => {
    setIsListening(true);

    if (!recognition) {
      return;
    }

    recognition.lang = currentLocale || "fr-FR";
    recognition.interimResults = false;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      return;
    };

    recognition.start();

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const voiceText = event.results[0][0].transcript;
      setText((prev) => (prev + " " + voiceText).trim().slice(0, 511));
    };

    recognition.onaudioend = () => {
      if (isListening) recognition.start();
    };
  }, [recognition, setIsListening, currentLocale, setText, isListening]);

  const checkMicroPhonePermission = useCallback(async () => {
    if (typeof navigator !== "undefined" && navigator.permissions) {
      const result = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      setMicroPhonePermission(result.state);

      result.onchange = () => {
        setMicroPhonePermission(result.state);
      };
    }
  }, [setMicroPhonePermission]);

  return {
    SpeechRecognition,
    isListening,
    microPhonePermission,
    requestMicrophonePermission,
    handleCutMicrophone,
    handleStartMicrophone,
    checkMicroPhonePermission,
  };
};
